// Estado y logica del telefono (independiente de React). Los componentes se suscriben con
// usePhone(). Aqui vive todo lo que el softphone anterior hacia, mas lo nuevo de la Consola:
// llamada en espera, transferencia con consulta, presencia local y tipificacion en vivo.

import { buscarPorNumero, clicParaLlamar, registrarLlamada, tipificar, type ReporteLlamada } from '../api/crm';
import { accionPuente } from '../api/puente';
import { ApiError, getSession } from '../api/http';
import { miCuentaVoz, TEXTO_ESTADO } from '../api/voz';
import { fijarVolumenTimbre, keyTone, startRingtone, stopRingtone, waitingBeep } from '../lib/audio';
import { avisarLlamada, quitarAvisoLlamada } from '../lib/avisoLlamada';

// Extension de 3CX (1 a 5 digitos, como esExtension de la app 3CX): llamada interna, no un cliente (D69).
export const esInterna = (numero: string) => !String(numero ?? '').trim().startsWith('+') && /^\d{1,5}$/.test(String(numero ?? '').replace(/[\s-]/g, ''));

// El timbre y su aviso fuera de la pestaña (D68) se apagan juntos.
const pararTimbre = () => {
  stopRingtone();
  quitarAvisoLlamada();
};
import { dialable, formatPhone, searchKey } from '../lib/phone-number';
import { KEYS, readJSON, writeJSON } from '../lib/storage';
import { configurado, loadConfig, saveConfig, type PhoneConfig } from './config';
import type { CallHandle, EndInfo, Line, LineStatus } from './line';
import { SimLine } from './sim-line';
import { aplicarAltavoz, SipLine } from './sip-line';

export type Contacto = { id: string; nombre: string; empresa?: string } | null;

export type Vista = 'main' | 'keypad' | 'transfer' | 'conf' | 'tip' | 'notes';

export type Llamada = {
  handle: CallHandle;
  dir: 'in' | 'out';
  numero: string;
  contacto: Contacto;
  estado: 'sonando' | 'marcando' | 'timbrando' | 'activa';
  creado: number;
  inicio: number | null;
  silencio: boolean;
  espera: boolean;
  notas: string;
  tipificacionId: string | null;
  dtmf: string;
  vista: Vista;
  consulta: null | { handle: CallHandle; numero: string; nombre: string; estado: 'timbrando' | 'activa' };
};

export type Terminada = {
  local: string;
  dir: 'in' | 'out';
  numero: string;
  contacto: Contacto;
  inicio: number;
  duracion: number;
  contestada: boolean;
  tipificacionId: string | null;
  notas: string;
  simulada: boolean; // llamada del modo demostracion: no se guarda en el CRM
  registro?: Promise<string | null>;
};

export type Reciente = {
  local: string;
  numero: string;
  nombre: string;
  empresa?: string;
  dir: 'in' | 'out';
  fecha: number;
  duracion: number;
  perdida: boolean;
  tipificacion?: string;
};

export type Presencia = 'disponible' | 'ocupado' | 'ausente' | 'dnd' | 'pausa';

export const PRESENCIAS: Record<Presencia, { nombre: string; detalle: string }> = {
  disponible: { nombre: 'Disponible', detalle: 'Recibes llamadas' },
  ocupado: { nombre: 'Ocupado', detalle: 'Recibes llamadas; tu equipo ve que estás ocupado' },
  ausente: { nombre: 'Ausente', detalle: 'Fuera del puesto por un momento' },
  dnd: { nombre: 'No molestar', detalle: 'El CRM no suena. Con el Puente XmarteK, 3CX también te pone en No molestar' },
  pausa: { nombre: 'En pausa', detalle: 'Almuerzo o descanso; el CRM no suena (en 3CX: estado personalizado 2)' },
};

export type ModoIsla = 'pill' | 'panel' | 'banner';
export type Pestana = 'teclado' | 'recientes' | 'buzon' | 'estado' | 'ajustes';

export type PhoneState = {
  config: PhoneConfig;
  lineStatus: LineStatus;
  lineDetail: string;
  presencia: Presencia;
  isla: ModoIsla;
  pestana: Pestana;
  marcador: string;
  call: Llamada | null;
  segunda: Llamada | null; // otra llamada en espera (llamada en espera contestada)
  entrante2: { handle: CallHandle; numero: string; contacto: Contacto } | null; // timbrando mientras hablas
  cierre: Terminada | null; // hoja "¿Como termino la llamada?"
  colaCierre: Terminada[];
  internaReciente: Terminada | null; // interna sin tipificar: boton opcional "Tipificar" unos segundos (D69)
  recientes: Reciente[];
  aviso: string;
};

type Listener = () => void;
type Evento = { tipo: 'abrir-ficha'; personaId: string } | { tipo: 'toast'; texto: string; icono?: 'ok' | 'info' | 'error' } | { tipo: 'registrada' };

const now = () => Date.now();
let localSeq = 0;

export class PhoneStore {
  private state: PhoneState;
  private listeners = new Set<Listener>();
  private eventos = new Set<(e: Evento) => void>();
  private line: Line;
  private sim = new SimLine();
  private sip = new SipLine();
  private offLine: (() => void)[] = [];
  private busquedas = new Map<string, Promise<Contacto>>();

  constructor() {
    const config = loadConfig();
    fijarVolumenTimbre(config.volumenTimbre);
    this.state = {
      config,
      lineStatus: configurado(config) ? 'desconectado' : 'sin-configurar',
      lineDetail: configurado(config) ? '' : 'Configura el teléfono en Ajustes.',
      presencia: readJSON<Presencia>('consola.presencia', 'disponible'),
      isla: 'pill',
      pestana: configurado(config) ? 'teclado' : 'ajustes',
      marcador: '',
      call: null,
      segunda: null,
      entrante2: null,
      cierre: null,
      colaCierre: [],
      internaReciente: null,
      recientes: readJSON<Reciente[]>(KEYS.recientes, []),
      aviso: '',
    };
    this.line = config.modo === 'demo' ? this.sim : this.sip;
    this.bindLine();
  }

  /* ---------- suscripcion ---------- */
  getState = () => this.state;
  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => {
      this.listeners.delete(l);
    };
  };
  onEvento(cb: (e: Evento) => void) {
    this.eventos.add(cb);
    return () => {
      this.eventos.delete(cb);
    };
  }
  private emit(e: Evento) {
    this.eventos.forEach((cb) => cb(e));
  }
  private set(patch: Partial<PhoneState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }
  private patchCall(patch: Partial<Llamada>) {
    if (this.state.call) this.set({ call: { ...this.state.call, ...patch } });
  }
  private toast(texto: string, icono: 'ok' | 'info' | 'error' = 'ok') {
    this.emit({ tipo: 'toast', texto, icono });
  }

  /* ---------- linea ---------- */
  private bindLine() {
    this.offLine.forEach((f) => f());
    this.offLine = [
      this.line.onStatus((s, d) => {
        this.set({ lineStatus: s, lineDetail: d });
        this.vigilar3cx(s);
      }),
      this.line.onIncoming((c) => this.incoming(c)),
    ];
  }

  // El navegador puede estar conectado a la pasarela mientras 3CX rechaza a la pasarela: en ese caso el
  // telefono NO esta disponible. Con cuenta aprovisionada por el servidor se consulta el registro real en 3CX
  // al conectar y cada minuto (solo con sesion de usuario; con API key no hay cuenta por agente).
  private relojVoz: ReturnType<typeof setInterval> | null = null;
  private vigilar3cx(s: LineStatus) {
    if (this.relojVoz) clearInterval(this.relojVoz);
    this.relojVoz = null;
    if (s !== 'en-linea' || this.state.config.modo !== 'web' || getSession()?.modo !== 'usuario') return;
    const revisar = async () => {
      try {
        const r = await miCuentaVoz();
        if (this.line.status !== 'en-linea' && this.state.lineStatus !== 'error') return;
        if (!r.configurado) return;
        if (r.estado === 'REGISTRADO') {
          if (this.state.lineStatus === 'error') this.set({ lineStatus: 'en-linea', lineDetail: '' });
        } else {
          this.set({ lineStatus: 'error', lineDetail: `${TEXTO_ESTADO[r.estado]}.` });
        }
      } catch {
        /* sin respuesta de la API de voz: se conserva el estado que da la linea */
      }
    };
    void revisar();
    this.relojVoz = setInterval(revisar, 60_000);
  }

  get esDemo() {
    return this.state.config.modo === 'demo';
  }

  connect() {
    const c = this.state.config;
    if (c.modo === '3cx') {
      this.set({ lineStatus: c.extension ? 'en-linea' : 'sin-configurar', lineDetail: c.extension ? `Marca desde tu extensión ${c.extension} de 3CX` : 'Indica tu extensión 3CX.' });
      return;
    }
    if (!configurado(c)) {
      this.set({ lineStatus: 'sin-configurar', lineDetail: 'Configura el teléfono en Ajustes.' });
      return;
    }
    this.line.connect({ ws: c.ws, usuario: c.usuario, clave: c.clave, nombre: c.nombre });
  }

  disconnect() {
    if (this.state.call) return this.toast('Cuelga antes de desconectar el teléfono.', 'info');
    this.line.disconnect();
  }

  saveConfig(c: PhoneConfig) {
    const antes = this.state.config;
    saveConfig(c);
    fijarVolumenTimbre(c.volumenTimbre);
    this.set({ config: c });
    if (antes.modo !== c.modo) {
      this.line.disconnect(true);
      this.line = c.modo === 'demo' ? this.sim : this.sip;
      this.bindLine();
    }
    this.connect();
  }

  // Preferencias que no cambian la conexion (dispositivos de audio, prueba hecha): se guardan sin reconectar,
  // asi no se corta una llamada en curso.
  guardarPreferencias(p: Partial<Pick<PhoneConfig, 'microfono' | 'altavoz' | 'ensayoHecho'>>) {
    const c = { ...this.state.config, ...p };
    saveConfig(c);
    this.set({ config: c });
    if ('altavoz' in p) aplicarAltavoz(c.altavoz);
  }

  setPresencia(p: Presencia) {
    writeJSON('consola.presencia', p);
    this.set({ presencia: p });
  }

  /* ---------- isla ---------- */
  expand(pestana?: Pestana) {
    if (this.state.call?.estado === 'sonando') return this.set({ isla: 'banner' });
    this.set({ isla: 'panel', ...(pestana ? { pestana } : {}) });
  }
  collapse() {
    if (this.state.call?.estado === 'sonando') return;
    this.set({ isla: 'pill' });
  }
  toggle() {
    if (this.state.isla === 'pill') this.expand();
    else this.collapse();
  }
  setPestana(p: Pestana) {
    this.set({ pestana: p, isla: 'panel' });
  }
  setMarcador(v: string) {
    this.set({ marcador: v });
  }
  tecla(k: string) {
    keyTone(k);
    if (this.state.call?.estado === 'activa') return this.dtmf(k);
    this.set({ marcador: this.state.marcador + k });
  }
  borrar() {
    this.set({ marcador: this.state.marcador.slice(0, -1) });
  }

  /* ---------- contactos ---------- */
  buscar(numero: string): Promise<Contacto> {
    const k = searchKey(numero);
    if (k.length < 7) return Promise.resolve(null);
    if (!this.busquedas.has(k)) {
      this.busquedas.set(
        k,
        buscarPorNumero(numero)
          .then((c) => (c ? { id: c.id, nombre: `${c.nombre ?? ''} ${c.apellido ?? ''}`.trim() || formatPhone(numero), empresa: c.empresa } : null))
          .catch(() => null),
      );
      setTimeout(() => this.busquedas.delete(k), 60_000);
    }
    return this.busquedas.get(k)!;
  }

  /* ---------- llamadas salientes ---------- */
  async dial(destino?: string, contacto?: Contacto) {
    const numero = dialable(destino ?? this.state.marcador);
    if (!numero) {
      this.expand('teclado');
      return;
    }
    const c = this.state.config;
    if (c.modo === '3cx') {
      if (!c.extension) {
        this.expand('ajustes');
        return this.toast('Indica tu extensión 3CX en Ajustes para usar clic para llamar.', 'info');
      }
      try {
        // D80: con el Puente XmarteK marca la propia central (sin la API de pago de 3CX); si la central no lo tiene,
        // se usa la API de control de llamadas de 3CX como antes.
        try {
          await accionPuente('llamar', { destino: numero });
        } catch (e) {
          if (!/Puente XmarteK|no tiene el puente/i.test((e as Error).message)) throw e;
          await clicParaLlamar(numero, c.extension);
        }
        this.pushReciente({ numero, nombre: contacto?.nombre ?? '', empresa: contacto?.empresa, dir: 'out', duracion: 0, perdida: false });
        this.toast(`Contesta tu teléfono 3CX: te conectamos con ${contacto?.nombre || formatPhone(numero)}.`);
      } catch (e) {
        this.toast((e as Error).message, 'error');
      }
      return;
    }
    if (this.state.call) {
      this.expand();
      return this.toast(this.state.call.estado === 'sonando' ? 'Primero contesta o rechaza la llamada que entra.' : 'Ya estás en una llamada. Usa Transferir o cuelga primero.', 'info');
    }
    if (this.line.status !== 'en-linea') {
      this.expand('ajustes');
      return this.toast(this.state.lineDetail || 'El teléfono no está en línea.', 'error');
    }
    let handle: CallHandle;
    try {
      handle = this.line.call(numero);
    } catch (e) {
      return this.toast((e as Error).message, 'error');
    }
    const call: Llamada = this.nuevaLlamada(handle, 'out', numero, contacto ?? null, 'marcando');
    this.set({ call, marcador: '', isla: 'panel' });
    this.wire(call);
    if (!contacto) this.buscar(numero).then((ct) => ct && this.state.call?.handle === handle && this.patchCall({ contacto: ct }));
  }

  private nuevaLlamada(handle: CallHandle, dir: 'in' | 'out', numero: string, contacto: Contacto, estado: Llamada['estado']): Llamada {
    return { handle, dir, numero, contacto, estado, creado: now(), inicio: null, silencio: false, espera: false, notas: '', tipificacionId: null, dtmf: '', vista: 'main', consulta: null };
  }

  private wire(call: Llamada) {
    const h = call.handle;
    h.on('progress', () => {
      if (this.state.call?.handle === h && call.dir === 'out') this.patchCall({ estado: 'timbrando' });
    });
    h.on('accepted', () => {
      pararTimbre();
      if (this.state.call?.handle === h) this.patchCall({ estado: 'activa', inicio: now() });
      else if (this.state.segunda?.handle === h) this.set({ segunda: { ...this.state.segunda, estado: 'activa', inicio: this.state.segunda.inicio ?? now() } });
    });
    const fin = (info: EndInfo) => this.ended(h, info);
    h.on('ended', fin);
    h.on('failed', fin);
  }

  /* ---------- llamadas entrantes ---------- */
  private incoming(handle: CallHandle) {
    const numero = handle.remote;
    const s = this.state;
    // No molestar / En pausa (D67): el CRM no suena, pero la llamada sigue en la app y el telefono de 3CX (480). Antes
    // respondia ocupado. 3CX registra el resultado real de la llamada; aqui no se anota como perdida.
    if (s.presencia === 'dnd' || s.presencia === 'pausa') {
      handle.reject(480);
      return;
    }
    if (s.call) {
      if (s.entrante2 || s.segunda) {
        handle.reject(480);
        return;
      }
      waitingBeep();
      this.set({ entrante2: { handle, numero, contacto: null } });
      this.buscar(numero).then((ct) => ct && this.state.entrante2?.handle === handle && this.set({ entrante2: { ...this.state.entrante2, contacto: ct } }));
      const fin = () => {
        if (this.state.entrante2?.handle === handle) {
          this.set({ entrante2: null });
          this.pushReciente({ numero, nombre: '', dir: 'in', duracion: 0, perdida: true });
          this.registrar({ local: `r${++localSeq}`, dir: 'in', numero, contacto: null, inicio: now(), duracion: 0, contestada: false, tipificacionId: null, notas: '', simulada: this.esDemo });
        }
      };
      handle.on('ended', fin);
      handle.on('failed', fin);
      return;
    }
    const call = this.nuevaLlamada(handle, 'in', numero, null, 'sonando');
    this.set({ call, isla: 'banner' });
    this.wire(call);
    if (s.config.sonarTimbre) startRingtone();
    avisarLlamada('', numero);
    this.buscar(numero).then((ct) => {
      if (!ct || this.state.call?.handle !== handle) return;
      this.patchCall({ contacto: ct });
      if (this.state.call?.estado === 'sonando') avisarLlamada(ct.empresa ? `${ct.nombre} (${ct.empresa})` : ct.nombre, numero);
      if (this.state.config.abrirFicha === 'sonar') this.emit({ tipo: 'abrir-ficha', personaId: ct.id });
    });
  }

  answer() {
    const c = this.state.call;
    if (!c || c.estado !== 'sonando') return;
    pararTimbre();
    c.handle.answer();
    this.set({ isla: 'panel', call: { ...c, estado: 'activa', inicio: now() } });
    if (c.contacto && this.state.config.abrirFicha === 'contestar') this.emit({ tipo: 'abrir-ficha', personaId: c.contacto.id });
  }

  reject() {
    const c = this.state.call;
    if (!c || c.estado !== 'sonando') return;
    pararTimbre();
    c.handle.reject();
  }

  // Llamada en espera: contestar la segunda deja la primera en espera.
  answerWaiting() {
    const w = this.state.entrante2;
    const c = this.state.call;
    if (!w || !c) return;
    c.handle.setHold(true);
    const nueva = this.nuevaLlamada(w.handle, 'in', w.numero, w.contacto, 'activa');
    nueva.inicio = now();
    this.set({ segunda: { ...c, espera: true, vista: 'main' }, call: nueva, entrante2: null, isla: 'panel' });
    this.wire(nueva);
    w.handle.answer();
  }

  rejectWaiting() {
    this.state.entrante2?.handle.reject();
  }

  // Alterna entre la llamada actual y la que esta en espera.
  swap() {
    const { call, segunda } = this.state;
    if (!call || !segunda) return;
    call.handle.setHold(true);
    segunda.handle.setHold(false);
    segunda.handle.playAudio();
    this.set({ call: { ...segunda, espera: false }, segunda: { ...call, espera: true, vista: 'main' } });
  }

  hangup() {
    const c = this.state.call;
    if (!c) return;
    pararTimbre();
    if (c.consulta) c.consulta.handle.hangup();
    c.handle.hangup();
  }

  /* ---------- controles en llamada ---------- */
  toggleMute() {
    const c = this.state.call;
    if (!c || c.estado !== 'activa') return;
    c.handle.setMuted(!c.silencio);
    this.patchCall({ silencio: !c.silencio });
  }

  toggleHold() {
    const c = this.state.call;
    if (!c || c.estado !== 'activa' || c.consulta) return;
    c.handle.setHold(!c.espera);
    if (c.espera) c.handle.playAudio(); // al reanudar, su audio vuelve a los parlantes
    this.patchCall({ espera: !c.espera });
  }

  dtmf(k: string) {
    const c = this.state.call;
    if (!c || c.estado !== 'activa') return;
    c.handle.sendDtmf(k);
    keyTone(k);
    this.patchCall({ dtmf: (c.dtmf + k).slice(-24) });
  }

  setVista(v: Vista) {
    if (!this.state.call) return;
    this.patchCall({ vista: v });
    this.set({ isla: 'panel' });
  }

  setNotas(t: string) {
    this.patchCall({ notas: t });
  }

  setTipificacion(id: string | null) {
    this.patchCall({ tipificacionId: id });
  }

  async transferBlind(destino: string) {
    const c = this.state.call;
    const d = dialable(destino);
    if (!c || !d) return;
    try {
      // D80: con el Puente XmarteK la transferencia la hace 3CX misma (la pasarela no siempre acepta el REFER).
      const porPuente = await accionPuente('transferir', { destino: d }).then(
        () => true,
        (e) => {
          if (/Puente XmarteK|no tiene el puente/i.test((e as Error).message)) return false;
          throw e;
        },
      );
      if (!porPuente) await c.handle.transferBlind(d);
      this.toast(`Transferida a ${formatPhone(d)}.`);
    } catch (e) {
      this.toast((e as Error).message, 'error');
    }
  }

  // Transferencia con consulta: el cliente queda en espera mientras hablas con el destino.
  startConsult(destino: string, nombre?: string) {
    const c = this.state.call;
    const d = dialable(destino);
    if (!c || !d || c.consulta || this.esDemo === false && this.line.status !== 'en-linea') return;
    c.handle.setHold(true);
    let h: CallHandle;
    try {
      h = this.line.call(d);
    } catch (e) {
      c.handle.setHold(false);
      return this.toast((e as Error).message, 'error');
    }
    this.patchCall({ espera: true, consulta: { handle: h, numero: d, nombre: nombre || formatPhone(d), estado: 'timbrando' } });
    h.on('accepted', () => {
      const cc = this.state.call;
      if (cc?.consulta?.handle === h) this.patchCall({ consulta: { ...cc.consulta, estado: 'activa' } });
      h.playAudio();
    });
    const fin = () => {
      const cc = this.state.call;
      if (cc?.consulta?.handle !== h) return;
      this.patchCall({ consulta: null });
      this.toast(`${cc.consulta.nombre} colgó. Sigues con ${cc.contacto?.nombre || formatPhone(cc.numero)} en espera.`, 'info');
    };
    h.on('ended', fin);
    h.on('failed', fin);
  }

  async completeConsult() {
    const c = this.state.call;
    if (!c?.consulta || c.consulta.estado !== 'activa') return;
    const nombre = c.consulta.nombre;
    try {
      await c.handle.transferAttended(c.consulta.handle);
      this.toast(`Transferencia completada con ${nombre}.`);
    } catch (e) {
      this.toast((e as Error).message, 'error');
    }
  }

  cancelConsult() {
    const c = this.state.call;
    if (!c?.consulta) return;
    const h = c.consulta.handle;
    this.patchCall({ consulta: null, espera: false });
    h.hangup();
    c.handle.setHold(false);
    c.handle.playAudio();
  }

  /* ---------- fin de llamada, registro y tipificacion ---------- */
  private ended(handle: CallHandle, _info: EndInfo) {
    pararTimbre();
    const { call, segunda } = this.state;
    const cual = call?.handle === handle ? call : segunda?.handle === handle ? segunda : null;
    if (!cual) return;
    const esPrincipal = cual === call;
    const duracion = cual.inicio ? Math.round((now() - cual.inicio) / 1000) : 0;
    const t: Terminada = {
      local: `r${++localSeq}`,
      dir: cual.dir,
      numero: cual.numero,
      contacto: cual.contacto,
      inicio: cual.inicio ?? cual.creado,
      duracion,
      contestada: !!cual.inicio,
      tipificacionId: cual.tipificacionId,
      notas: cual.notas,
      simulada: this.esDemo,
    };
    if (cual.consulta) cual.consulta.handle.hangup();

    if (esPrincipal) {
      // Si habia otra llamada en espera, pasa a ser la principal (sigue en espera hasta reanudarla).
      this.set({ call: segunda ? { ...segunda } : null, segunda: null, isla: segunda ? 'panel' : this.state.isla === 'banner' ? 'pill' : this.state.isla });
    } else {
      this.set({ segunda: null });
    }
    this.pushReciente({
      numero: t.numero,
      nombre: t.contacto?.nombre ?? '',
      empresa: t.contacto?.empresa,
      dir: t.dir,
      duracion: t.duracion,
      perdida: t.dir === 'in' && !t.contestada,
    }, t.local);
    t.registro = this.registrar(t);
    if (t.tipificacionId) {
      void this.guardarTipificacion(t, t.tipificacionId, t.notas, true);
    } else if (t.contestada && t.duracion > 0) {
      if (esInterna(t.numero) && this.state.config.tipificarInternas !== 'siempre') this.ofrecerTipificarInterna(t);
      else this.abrirCierre(t);
    }
    if (!this.state.call && this.state.isla === 'panel') this.set({ pestana: 'teclado' });
    this.sacarCierrePendiente();
  }

  private registrar(t: Terminada): Promise<string | null> {
    if (t.simulada) return Promise.resolve(null);
    const tipo: ReporteLlamada['tipo'] = t.dir === 'in' ? (t.contestada ? 'Inbound' : 'Missed') : t.contestada ? 'Outbound' : 'Notanswered';
    return registrarLlamada({
      numero: t.numero,
      tipo,
      agente: this.state.config.extension || '',
      duracion: t.duracion,
      inicio: new Date(t.inicio).toISOString(),
      contactoId: t.contacto?.id,
    })
      .then((r) => {
        this.emit({ tipo: 'registrada' });
        return r.id ?? null;
      })
      .catch((e) => {
        // Sin la app Xmartek 3CX la ruta /s/3cx/llamada no existe (404): se avisa una vez, sin tono de error.
        if (e instanceof ApiError && e.status === 404) {
          if (!this.avisoSinApp) this.toast('Las llamadas aún no se guardan: falta instalar la integración 3CX en esta empresa.', 'info');
          this.avisoSinApp = true;
          return null;
        }
        this.toast(`La llamada no quedó registrada en el CRM: ${(e as Error).message}`, 'error');
        return null;
      });
  }
  private avisoSinApp = false;

  // Llamada interna (D69): no se exige tipificar. Queda un boton "Tipificar" 20 segundos por si la persona quiere.
  private esperaInterna: ReturnType<typeof setTimeout> | null = null;
  private ofrecerTipificarInterna(t: Terminada) {
    if (this.esperaInterna) clearTimeout(this.esperaInterna);
    this.set({ internaReciente: t });
    this.esperaInterna = setTimeout(() => this.state.internaReciente === t && this.set({ internaReciente: null }), 20_000);
  }
  tipificarInterna() {
    const t = this.state.internaReciente;
    if (!t) return;
    this.set({ internaReciente: null });
    this.abrirCierre(t);
  }
  descartarInterna = () => this.set({ internaReciente: null });

  // La hoja de cierre no tapa el telefono mientras sigue otra llamada: espera su turno.
  private abrirCierre(t: Terminada) {
    if (this.state.cierre || this.state.call) this.set({ colaCierre: [...this.state.colaCierre, t] });
    else this.set({ cierre: t });
  }

  private sacarCierrePendiente() {
    if (this.state.call || this.state.cierre || !this.state.colaCierre.length) return;
    const [sig, ...resto] = this.state.colaCierre;
    this.set({ cierre: sig, colaCierre: resto });
  }

  // Tipificar desde la lista de llamadas o "Por tipificar" (llamadas ya registradas).
  abrirCierreExistente(p: { llamadaId: string; numero: string; contacto: Contacto; dir: 'in' | 'out'; duracion: number; inicio: number; notas?: string }) {
    this.abrirCierre({
      local: `x${++localSeq}`,
      dir: p.dir,
      numero: p.numero,
      contacto: p.contacto,
      inicio: p.inicio,
      duracion: p.duracion,
      contestada: true,
      tipificacionId: null,
      notas: p.notas ?? '',
      simulada: false,
      registro: Promise.resolve(p.llamadaId),
    });
  }

  cerrarCierre(guardado = false) {
    this.set({ cierre: null });
    if (!guardado) this.toast('Queda pendiente en “Por tipificar”.', 'info');
    this.sacarCierrePendiente();
  }

  async guardarTipificacion(t: Terminada, tipificacionId: string, notas: string, silencioso = false) {
    if (t.simulada) {
      if (!silencioso) this.toast('Modo demostración: la tipificación no se guarda en el CRM.', 'info');
      return true;
    }
    const id = await (t.registro ?? Promise.resolve(null));
    if (!id) {
      this.toast(this.avisoSinApp ? 'La tipificación no se guarda todavía: falta instalar la integración 3CX en esta empresa.' : 'No se pudo tipificar: la llamada no quedó registrada en el CRM.', this.avisoSinApp ? 'info' : 'error');
      return false;
    }
    try {
      const r = await tipificar(id, tipificacionId, notas);
      this.marcarReciente(t.local, r.tipificacion);
      this.emit({ tipo: 'registrada' });
      if (!silencioso) this.toast(r.tareaId ? `Guardado: ${r.tipificacion}. Creamos la tarea de seguimiento para mañana.` : `Guardado: ${r.tipificacion}.`);
      return true;
    } catch (e) {
      this.toast(`No se pudo tipificar: ${(e as Error).message}`, 'error');
      return false;
    }
  }

  /* ---------- recientes locales ---------- */
  private pushReciente(r: Omit<Reciente, 'local' | 'fecha'>, local = `r${++localSeq}`) {
    const lista = [{ ...r, local, fecha: now() }, ...this.state.recientes].slice(0, 40);
    writeJSON(KEYS.recientes, lista);
    this.set({ recientes: lista });
  }
  private marcarReciente(local: string, tipificacion: string) {
    const lista = this.state.recientes.map((r) => (r.local === local ? { ...r, tipificacion } : r));
    writeJSON(KEYS.recientes, lista);
    this.set({ recientes: lista });
  }

  /* ---------- demostracion ---------- */
  simularEntrante() {
    if (!this.esDemo) {
      this.toast('Activa el modo demostración en Ajustes del teléfono para simular llamadas.', 'info');
      this.expand('ajustes');
      return;
    }
    if (this.line.status !== 'en-linea') this.connect();
    const numeros = ['+573104821937', '+576017453300', '+573001234567', '+573187740093'];
    const n = numeros[Math.floor((now() / 1000) % numeros.length)];
    this.sim.ring(n);
  }

  // El cliente cuelga (solo demostracion y pruebas).
  simularColgarRemoto() {
    const h = this.state.call?.handle as unknown as { remoteHangup?: () => void } | undefined;
    h?.remoteHangup?.();
  }
}

export const phone = new PhoneStore();
if (typeof window !== 'undefined') (window as unknown as { __consolaPhone?: PhoneStore }).__consolaPhone = phone;
